/**
 * API Client
 * Phase 6: Frontend Application
 *
 * Centralised API client with authentication support.
 * Automatically includes access tokens for authenticated requests.
 * Transforms API responses (snake_case) to frontend types (camelCase).
 */

import {
    transformRecipe,
    transformRecipeInput,
    transformRecipes,
    type ApiItemResponse,
    type ApiListResponse,
    type ApiRecipe,
} from './api-transformers';
import { apiTokenRequest, getMsalInstanceForApi, isAuthConfigured } from './auth';
import { logger } from './logger';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }

  get isUnauthorised(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isValidationError(): boolean {
    return this.status === 400;
  }
}

/**
 * Get access token for API calls
 */
async function getAccessToken(): Promise<string | null> {
  // Skip auth if not configured (development mode)
  if (!isAuthConfigured()) {
    logger.warn('Auth not configured - running in development mode without authentication');
    return null;
  }

  const msalInstance = getMsalInstanceForApi();
  if (!msalInstance) return null;

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    logger.warn('Failed to acquire token silently:', error);
    return null;
  }
}

/**
 * Make an authenticated API request
 * Default timeout is 30s; pass timeoutMs to override for long-running endpoints.
 */
async function request<T>(
  endpoint: string,
  options: RequestInit & { timeoutMs?: number } = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Get access token
  const token = await getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Add Authorization header if we have a token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? 30_000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
      signal: controller.signal,
    });
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Request timeout', { message: `Request timed out after ${timeoutMs / 1000}s` });
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new ApiError(response.status, response.statusText, data);
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return {} as T;
  }

  return response.json();
}

import type { CreateRecipeInput, NutritionData, Recipe, UpdateRecipeInput, ValidationResult } from '@/types/recipe';

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    cursor: string | null;
    hasMore: boolean;
  };
  meta: {
    timestamp: string;
  };
}

export interface RecipeStats {
  total: number;
  byStatus: Record<string, number>;
}

/**
 * Fix B — the API persists/returns allergens under the field name `allergies`,
 * but the web app's `UserPreferences` type and every consumer read `allergens`.
 * This normaliser maps `allergies` → `allergens` so stored allergens display.
 * Applied to EVERY `api.users.*` method that returns `UserPreferences`, because
 * the preferences page calls `setPreferences(result)` from mutation responses
 * too — mapping only on GET would let allergens vanish after an unrelated update.
 */
function normaliseUserPreferences(
  data: UserPreferences & { allergies?: UserAllergen[] }
): UserPreferences {
  if (!data) return data;
  const allergens = Array.isArray(data.allergens)
    ? data.allergens
    : Array.isArray(data.allergies)
      ? data.allergies
      : [];
  return { ...data, allergens };
}

export const api = {
  recipes: {
    validate: (data: CreateRecipeInput): Promise<ValidationResult> => {
      const apiData = transformRecipeInput(data as Parameters<typeof transformRecipeInput>[0]);
      return request<ValidationResult>('/recipes/validate', {
        method: 'POST',
        body: JSON.stringify(apiData),
      });
    },
    list: async (params?: { page?: number; limit?: number; search?: string; status?: string; healthTags?: string[]; mealTypes?: string[]; mode?: 'solo' | 'family' | 'auto' | 'none'; sort?: string; order?: 'asc' | 'desc' }): Promise<PaginatedResponse<Recipe>> => {
      // If search, healthTags, or mealTypes is provided, use /recipes/search endpoint
      // Note: Search endpoint supports filtering even with empty query, and now supports status
      if (params?.search || params?.healthTags?.length || params?.mealTypes?.length) {
        const searchParams = new URLSearchParams();
        if (params.search) searchParams.set('q', params.search);
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.limit) searchParams.set('limit', String(params.limit));
        if (params?.healthTags?.length) searchParams.set('health_tags', params.healthTags.join(','));
        if (params?.mealTypes?.length) searchParams.set('meal_types', params.mealTypes.join(','));
        if (params?.status) searchParams.set('status', params.status);
        if (params?.mode) searchParams.set('mode', params.mode);
        if (params?.sort) searchParams.set('sort', params.sort);
        if (params?.order) searchParams.set('order', params.order);

        const response = await request<ApiListResponse<ApiRecipe>>(
          `/recipes/search?${searchParams.toString()}`
        );

        return {
          data: transformRecipes(response.data),
          pagination: {
            cursor: response.pagination.cursor,
            hasMore: response.pagination.has_more,
          },
          meta: response.meta,
        };
      }

      // Otherwise use /recipes list endpoint
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.status) searchParams.set('status', params.status);
      if (params?.mode) searchParams.set('mode', params.mode);
      if (params?.sort) searchParams.set('sort', params.sort);
      if (params?.order) searchParams.set('order', params.order);
      const query = searchParams.toString();
      
      const response = await request<ApiListResponse<ApiRecipe>>(
        `/recipes${query ? `?${query}` : ''}`
      );
      
      return {
        data: transformRecipes(response.data),
        pagination: {
          cursor: response.pagination.cursor,
          hasMore: response.pagination.has_more,
        },
        meta: response.meta,
      };
    },
    getStats: async (): Promise<RecipeStats> => {
      const response = await request<{ data: RecipeStats; meta: { timestamp: string } }>('/recipes/stats');
      return response.data;
    },
    get: async (id: string): Promise<Recipe> => {
      const response = await request<ApiItemResponse<ApiRecipe>>(`/recipes/${id}`);
      return transformRecipe(response.data);
    },
    create: async (data: CreateRecipeInput): Promise<Recipe> => {
      const apiData = transformRecipeInput(data as Parameters<typeof transformRecipeInput>[0]);
      const response = await request<ApiItemResponse<ApiRecipe>>('/recipes', {
        method: 'POST',
        body: JSON.stringify(apiData),
      });
      return transformRecipe(response.data);
    },
    update: async (id: string, data: UpdateRecipeInput): Promise<Recipe> => {
      const apiData = transformRecipeInput(data as Parameters<typeof transformRecipeInput>[0]);
      const response = await request<ApiItemResponse<ApiRecipe>>(`/recipes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(apiData),
      });
      return transformRecipe(response.data);
    },
    delete: (id: string) =>
      request<void>(`/recipes/${id}`, { method: 'DELETE' }),
    duplicate: async (id: string): Promise<Recipe> => {
      const response = await request<ApiItemResponse<ApiRecipe>>(`/recipes/${id}/duplicate`, { method: 'POST' });
      return transformRecipe(response.data);
    },
    // Fix D: shopping list for a single recipe
    getShoppingList: (recipeId: string, servings?: number) => {
      const params = new URLSearchParams();
      if (servings && servings > 0) {
        params.append('servings', String(servings));
      }
      const queryString = params.toString();
      return request<RecipeShoppingListResponse>(
        `/recipes/${recipeId}/shopping-list${queryString ? `?${queryString}` : ''}`
      );
    },
    submit: async (id: string): Promise<Recipe> => {
      const response = await request<ApiItemResponse<ApiRecipe>>(`/recipes/${id}/submit`, { method: 'POST' });
      return transformRecipe(response.data);
    },
    publish: async (id: string): Promise<Recipe> => {
      const response = await request<ApiItemResponse<ApiRecipe>>(`/recipes/${id}/publish`, { method: 'POST' });
      return transformRecipe(response.data);
    },
    unpublish: async (id: string): Promise<Recipe> => {
      const response = await request<ApiItemResponse<ApiRecipe>>(`/recipes/${id}/unpublish`, { method: 'POST' });
      return transformRecipe(response.data);
    },
    // Phase 7.6 Task 7.6.5: Update user notes
    updateNotes: async (id: string, notes: string): Promise<{ id: string; user_notes: string; user_notes_updated_at: string }> => {
      const response = await request<ApiItemResponse<{ id: string; user_notes: string; user_notes_updated_at: string }>>(
        `/recipes/${id}/notes`,
        {
          method: 'PATCH',
          body: JSON.stringify({ notes }),
        }
      );
      return response.data;
    },
  },

  import: {
    fromUrl: (url: string) =>
      request<ImportResponse>('/recipes/import/url', {
        method: 'POST',
        body: JSON.stringify({ url }),
      }),
    fromText: (text: string) =>
      request<ImportResponse>('/recipes/import/text', {
        method: 'POST',
        body: JSON.stringify({ text }),
      }),
    fromImage: (image: string, mimeType: string) =>
      request<ImportResponse>('/recipes/import/image', {
        method: 'POST',
        body: JSON.stringify({ image, mimeType }),
      }),
    createBatch: (items: BatchImportItem[]): Promise<{ success: boolean; data: { batchId: string; message: string } }> =>
      request('/recipes/import/batch', {
        method: 'POST',
        body: JSON.stringify({ items }),
      }),
    getBatchStatus: (batchId: string): Promise<{ success: boolean; data: BatchImportStatus }> =>
      request(`/recipes/import/batch/${batchId}`),
    cancelBatch: (batchId: string): Promise<void> =>
      request(`/recipes/import/batch/${batchId}`, { method: 'DELETE' }),
  },

  enrichment: {
    enrich: async (recipeId: string, customInstructions?: string): Promise<EnrichmentResponse> => {
      const response = await request<{ data: EnrichmentResponse; meta: { timestamp: string } }>(`/enrichment/${recipeId}`, {
        method: 'POST',
        body: customInstructions ? JSON.stringify({ custom_instructions: customInstructions }) : undefined,
      });
      return response.data;
    },
    status: async (recipeId: string): Promise<EnrichmentStatusResponse> => {
      const response = await request<{ data: EnrichmentStatusResponse; meta: { timestamp: string } }>(`/enrichment/${recipeId}/status`);
      return response.data;
    },
    // Phase 7.8: Granular enrichment endpoints
    enrichNutrition: async (recipeId: string): Promise<GranularEnrichmentResponse> => {
      const response = await request<{ data: GranularEnrichmentResponse; meta: { timestamp: string } }>(`/enrichment/${recipeId}/nutrition`, {
        method: 'POST',
      });
      return response.data;
    },
    enrichStructure: async (recipeId: string): Promise<GranularEnrichmentResponse> => {
      const response = await request<{ data: GranularEnrichmentResponse; meta: { timestamp: string } }>(`/enrichment/${recipeId}/structure`, {
        method: 'POST',
      });
      return response.data;
    },
    enrichDescription: async (recipeId: string): Promise<GranularEnrichmentResponse> => {
      const response = await request<{ data: GranularEnrichmentResponse; meta: { timestamp: string } }>(`/enrichment/${recipeId}/description`, {
        method: 'POST',
      });
      return response.data;
    },
    enrichTags: async (recipeId: string): Promise<GranularEnrichmentResponse> => {
      const response = await request<{ data: GranularEnrichmentResponse; meta: { timestamp: string } }>(`/enrichment/${recipeId}/tags`, {
        method: 'POST',
      });
      return response.data;
    },
  },

  guides: {
    list: () =>
      request<GuidesListResponse>('/guides'),
    get: (id: string) =>
      request<GuideResponse>(`/guides/${id}`),
    effective: () =>
      request<GuideResponse>('/guides/effective'),
    create: (data: CreateGuideInput) =>
      request<GuideResponse>('/guides', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateGuideInput) =>
      request<GuideResponse>(`/guides/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<void>(`/guides/${id}`, { method: 'DELETE' }),
    clone: (id: string, name?: string) =>
      request<GuideResponse>(`/guides/${id}/clone`, {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    generate: (data: GenerateGuideInput) =>
      request<GuideResponse>('/guides/generate', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    distill: (id: string) =>
      request<GuideDistillResponse>(`/guides/${id}/distill`, {
        method: 'POST',
      }),
  },

  generation: {
    generate: (data: GenerateRecipesInput) =>
      request<GenerateRecipesResponse>('/recipes/generate', {
        method: 'POST',
        body: JSON.stringify(data),
        timeoutMs: 120_000, // 2 min — allows up to ~5 recipes × ~20s each
      }),
    // Persist an already-previewed generated recipe verbatim (no re-generation),
    // then trigger background enrichment server-side.
    saveGenerated: (recipe: GeneratedRecipe, enrich = true) =>
      request<SaveGeneratedRecipeResponse>('/recipes/generate/save', {
        method: 'POST',
        body: JSON.stringify({ recipe, enrich }),
      }),
    suggestions: () =>
      request<GenerationSuggestionsResponse>('/recipes/generate/suggestions'),
  },

  nutrition: {
    calculate: (recipeId: string) =>
      request<NutritionCalculationResponse>(`/nutrition/recipe/${recipeId}`, {
        method: 'POST',
      }),
    getLabel: (recipeId: string) =>
      request<UKNutritionLabel>(`/nutrition/label/${recipeId}`),
    search: (query: string) =>
      request<NutritionSearchResponse>(`/nutrition/search?q=${encodeURIComponent(query)}`),
  },

  review: {
    approve: (recipeId: string, notes?: string) =>
      request<Recipe>(`/review/${recipeId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ notes }),
      }),
    reject: (recipeId: string, reason: string) =>
      request<Recipe>(`/review/${recipeId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
  },

  menus: {
    list: (params?: { limit?: number; offset?: number }) =>
      request<MenuListResponse>(`/menus?limit=${params?.limit ?? 20}&offset=${params?.offset ?? 0}`),
    get: (id: string) =>
      request<MenuResponse>(`/menus/${id}`),
    create: (data: CreateMenuInput) =>
      request<MenuResponse>('/menus', {
        method: 'POST',
        body: JSON.stringify({
          name: data.name,
          description: data.description,
          num_days: data.numDays,
          num_people: data.numPeople,
          default_servings: data.servings,
          week_start_day: data.weekStartDay,
          guide_id: data.guideId,
          planned_meals: data.plannedMeals,
          start_date: data.startDate,
          end_date: data.endDate,
          planning_mode: data.planningMode,
          family_group_id: data.familyGroupId,
        }),
      }),
    fillMenuWithAI: (menuId: string, data: FillMenuWithAIInput) =>
      request<MenuResponse>(`/menus/${menuId}/fill-ai`, {
        method: 'POST',
        body: JSON.stringify({
          guide_id: data.guideId,
          prompt: data.prompt,
        }),
      }),
    // Phase 7.31: re-plan an existing menu while keeping locked meals
    adjust: (menuId: string, data: AdjustMenuInput) =>
      request<MenuResponse>(`/menus/${menuId}/adjust`, {
        method: 'POST',
        body: JSON.stringify({
          additional_instructions: data.additionalInstructions,
          locked_meal_ids: data.lockedMealIds,
        }),
      }),
    update: (id: string, data: UpdateMenuInput) =>
      request<MenuResponse>(`/menus/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: data.name,
          description: data.description,
          status: data.status,
          start_date: data.startDate,
          end_date: data.endDate,
        }),
      }),
    delete: (id: string) =>
      request<void>(`/menus/${id}`, { method: 'DELETE' }),
    addRecipe: (menuId: string, data: AddRecipeToMenuInput) =>
      request<MenuRecipeResponse>(`/menus/${menuId}/recipes`, {
        method: 'POST',
        body: JSON.stringify({
          day_number: data.dayNumber,
          recipe_id: data.recipeId,
          meal_type: data.mealType,
          servings: data.servings,
        }),
      }),
    removeRecipe: (itemId: string) =>
      request<void>(`/menus/items/${itemId}`, { method: 'DELETE' }),
    getShoppingList: (menuId: string, filters?: { days?: number[]; mealTypes?: string[] }) => {
      const params = new URLSearchParams();
      if (filters?.days?.length) {
        params.append('days', filters.days.join(','));
      }
      if (filters?.mealTypes?.length) {
        params.append('meal_types', filters.mealTypes.join(','));
      }
      const queryString = params.toString();
      return request<ShoppingListResponse>(
        `/menus/${menuId}/shopping-list${queryString ? `?${queryString}` : ''}`
      );
    },
    // Phase 6F: Advanced Meal Planning
    generate: (data: GenerateMenuInput) =>
      request<MenuResponse>('/menus/generate', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    // Returns null when no active menu exists (API returns 200 with data: null)
    getActive: () =>
      request<MenuResponse | null>('/menus/active'),
    activate: (id: string, dates?: { startDate: Date; endDate: Date }) =>
      request<MenuResponse>(`/menus/${id}/activate`, {
        method: 'POST',
        body: dates
          ? JSON.stringify({
              start_date: dates.startDate.toISOString(),
              end_date: dates.endDate.toISOString(),
            })
          : undefined,
      }),
    swapRecipe: (menuId: string, data: SwapRecipeInput) =>
      request<MenuResponse>(`/menus/${menuId}/swap`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    getAlternatives: (itemId: string) =>
      request<RecipeAlternative[]>(`/menus/items/${itemId}/alternatives`),
    getNutrition: (menuId: string) =>
      request<MenuNutrition>(`/menus/${menuId}/nutrition`),
    saveAsTemplate: (data: { menuId: string; name: string; description?: string }) =>
      request<{ success: boolean; data: { id: string; name: string } }>('/menus/templates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  users: {
    getPreferences: async (): Promise<UserPreferences> => {
      const response = await request<{ success: boolean; data: UserPreferences }>('/users/preferences');
      return normaliseUserPreferences(response.data);
    },
    updatePreferences: async (data: Partial<UserPreferences>): Promise<UserPreferences> => {
      const response = await request<{ success: boolean; data: UserPreferences }>('/users/preferences', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return normaliseUserPreferences(response.data);
    },
    addAllergy: async (data: { allergen: string; severity: string }): Promise<UserPreferences> => {
      const response = await request<{ success: boolean; data: UserPreferences }>('/users/preferences/allergies', {
        method: 'POST',
        body: JSON.stringify({ allergies: [data] }), // Backend expects array
      });
      return normaliseUserPreferences(response.data);
    },
    removeAllergy: async (allergen: string): Promise<UserPreferences> => {
      const response = await request<{ success: boolean; data: UserPreferences }>(`/users/preferences/allergies/${encodeURIComponent(allergen)}`, {
        method: 'DELETE',
      });
      return normaliseUserPreferences(response.data);
    },
    /**
     * Phase 8.0 (UX follow-up) — add liked ingredients via the dedicated POST
     * endpoint so the catalogue validation block is surfaced to the caller.
     * The page renders "Did you mean…?" / "Couldn't recognise…" notices from
     * `validation.suggestions` / `validation.rejected`. Pass `force=true` to
     * accept verbatim (`?force=1` query, stored with `validated=false`).
     */
    addLikes: async (
      ingredients: string[],
      options: { force?: boolean } = {}
    ): Promise<IngredientAddResult> => {
      const qs = options.force ? '?force=1' : '';
      const response = await request<IngredientAddApiResponse>(`/users/preferences/likes${qs}`, {
        method: 'POST',
        body: JSON.stringify({ ingredients }),
      });
      return {
        preferences: normaliseUserPreferences(response.data),
        validation: response.validation,
      };
    },
    /** Phase 8.0 (UX follow-up) — counterpart to `addLikes` for dislikes. */
    addDislikes: async (
      ingredients: string[],
      options: { force?: boolean } = {}
    ): Promise<IngredientAddResult> => {
      const qs = options.force ? '?force=1' : '';
      const response = await request<IngredientAddApiResponse>(`/users/preferences/dislikes${qs}`, {
        method: 'POST',
        body: JSON.stringify({ ingredients }),
      });
      return {
        preferences: normaliseUserPreferences(response.data),
        validation: response.validation,
      };
    },
    /**
     * Phase 7.39.9 / Phase 8.0 — add intolerances (ingredients tolerated in
     * small amounts). Surfaces the Phase 8.0 catalogue validation block so
     * unknown / typo / dialect inputs can be flagged in the UI rather than
     * silently dropped.
     */
    addIntolerances: async (
      ingredients: string[],
      options: { force?: boolean } = {}
    ): Promise<IngredientAddResult> => {
      const qs = options.force ? '?force=1' : '';
      const response = await request<IngredientAddApiResponse>(`/users/preferences/intolerances${qs}`, {
        method: 'POST',
        body: JSON.stringify({ ingredients }),
      });
      return {
        preferences: normaliseUserPreferences(response.data),
        validation: response.validation,
      };
    },
    /** Phase 7.39.9 — remove a single intolerance */
    removeIntolerance: async (ingredient: string): Promise<UserPreferences> => {
      const response = await request<{ success: boolean; data: UserPreferences }>(`/users/preferences/intolerances/${encodeURIComponent(ingredient)}`, {
        method: 'DELETE',
      });
      return normaliseUserPreferences(response.data);
    },
    updateMeasurementDisplay: async (display: string): Promise<UserPreferences> => {
      const response = await request<{ success: boolean; data: UserPreferences }>('/users/preferences/measurement-display', {
        method: 'PUT',
        body: JSON.stringify({ display }),
      });
      return normaliseUserPreferences(response.data);
    },
    getFavourites: async () => {
      const response = await request<{ success: boolean; data: UserFavourite[]; pagination?: unknown }>('/users/favourites');
      return response.data || [];
    },
    addFavourite: (recipeId: string) =>
      request<UserFavourite>(`/users/favourites/${recipeId}`, {
        method: 'POST',
      }),
    removeFavourite: (recipeId: string) =>
      request<void>(`/users/favourites/${recipeId}`, { method: 'DELETE' }),
  },

  variants: {
    list: (recipeId: string) =>
      request<{ data: unknown[] }>(`/recipes/${recipeId}/variants`),
    generate: (recipeId: string, data: { variant_type: string; variant_description: string }) =>
      request<{ data: unknown }>(`/recipes/${recipeId}/variants/generate`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    link: (parentId: string, variantId: string) =>
      request<{ data: unknown }>(`/recipes/${parentId}/variants/link`, {
        method: 'POST',
        body: JSON.stringify({ variant_id: variantId }),
      }),
  },

  health: {
    check: () => request<HealthCheckResponse>('/health'),
    ready: () => request<HealthCheckResponse>('/health/ready'),
  },

  family: {
    list: () => request<{ success: boolean; data: FamilyGroup[]; count: number }>('/family-groups'),
    // Phase 7.40.8 — pending invitations for the current user. Used by the
    // header invitations bell to drive its unread-count badge.
    invitations: () =>
      request<{ success: boolean; data: FamilyInvitation[]; count: number }>(
        '/family-groups/invitations'
      ),
    acceptInvitation: (invitationId: string) =>
      request<{ success: boolean; data: FamilyMember; message: string }>(
        `/family-groups/invitations/${invitationId}/accept`,
        { method: 'POST' }
      ),
    declineInvitation: (invitationId: string) =>
      request<{ success: boolean; message: string }>(
        `/family-groups/invitations/${invitationId}/decline`,
        { method: 'POST' }
      ),
    get: (id: string) => request<{ success: boolean; data: FamilyGroup }>(`/family-groups/${id}`),
    create: (data: { name: string }) =>
      request<{ success: boolean; data: FamilyGroup }>('/family-groups', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: { name?: string }) =>
      request<{ success: boolean; data: FamilyGroup }>(`/family-groups/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/family-groups/${id}`, { method: 'DELETE' }),
    invite: (groupId: string, email: string) =>
      request<{ success: boolean; data: FamilyInvitation }>(`/family-groups/${groupId}/invite`, {
        method: 'POST',
        body: JSON.stringify({ invitee_email: email }),
      }),
    removeMember: (groupId: string, userId: string) =>
      request<{ success: boolean }>(`/family-groups/${groupId}/members/${userId}`, {
        method: 'DELETE',
      }),
    // Phase 7.40.9 — current user leaves the group. When the caller is the
    // last admin AND no action is supplied the API returns 400 with
    // `details.requires_admin_choice` so the UI can present a single
    // promote-or-disband dialog. Re-submitting with an action either promotes
    // a chosen member and leaves, or disbands the group entirely (cascading
    // to active menus) and reports the deleted count back.
    leave: (
      groupId: string,
      action?:
        | { action: 'promote'; new_admin_user_id: string }
        | { action: 'disband' }
    ) =>
      request<{ success: boolean; message: string; deleted_menus?: number }>(
        `/family-groups/${groupId}/leave`,
        {
          method: 'POST',
          ...(action ? { body: JSON.stringify(action) } : {}),
        }
      ),
    // Phase 7.40.4 — promote a member to admin (idempotent).
    promote: (groupId: string, userId: string) =>
      request<{ success: boolean; message: string }>(`/family-groups/${groupId}/members/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: 'admin' }),
      }),
    // Phase 7.40.4 — destructive: disbands the group (cascades to members
    // and active Menus via FK ON DELETE CASCADE). Admin-only.
    disband: (groupId: string) =>
      request<{ success: boolean }>(`/family-groups/${groupId}`, { method: 'DELETE' }),
    // Phase 7.40.6 — aggregated allergens + shared likes/dislikes across
    // all accepted members of a group.
    getAggregatedPreferences: (groupId: string) =>
      request<{ success: boolean; data: unknown }>(
        `/family-groups/${groupId}/preferences`
      ),
  },

  export: {
    createJob: (data: CreateExportJobInput) =>
      request<{ data: { jobId: string; status: string }; meta: { timestamp: string } }>('/export/jobs', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    getJobStatus: (jobId: string) =>
      request<{ data: ExportJobStatusResponse; meta: { timestamp: string } }>(`/export/jobs/${jobId}`),
    getDownloadUrl: (token: string): string => {
      // API_BASE_URL already contains /api/v1 — do NOT add it again here
      return `${API_BASE_URL}/export/download/${token}`;
    },
  },

  // Phase 8.0.13 — admin moderation of the canonical ingredient catalogue.
  // RBAC enforced server-side (admin | trusted_editor). Non-admin calls 403.
  admin: {
    listIngredientQueue: (params?: {
      status?: 'pending' | 'approved' | 'rejected';
      source?: 'seed' | 'llm_promoted' | 'admin_added';
      limit?: number;
    }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set('status', params.status);
      if (params?.source) q.set('source', params.source);
      if (params?.limit) q.set('limit', String(params.limit));
      const qs = q.toString();
      return request<{
        data: Array<{
          kind: 'canonical' | 'alias';
          id: string;
          name: string;
          parent_canonical: string | null;
          category: string;
          is_category: boolean;
          common_allergens_json: string;
          confidence: number;
          original_input: string | null;
          promoting_user_id: string | null;
          usage_count: number;
          created_at: string;
        }>;
        meta: { count: number; status: string };
      }>(`/admin/ingredient-moderation/queue${qs ? `?${qs}` : ''}`);
    },
    approveCanonical: (id: string) =>
      request<{ data: { id: string; status: 'approved' } }>(
        `/admin/ingredient-moderation/canonical/${id}/approve`,
        { method: 'POST' }
      ),
    rejectCanonical: (id: string) =>
      request<{ data: { id: string; status: 'rejected' } }>(
        `/admin/ingredient-moderation/canonical/${id}/reject`,
        { method: 'POST' }
      ),
    approveAlias: (id: string) =>
      request<{ data: { id: string; status: 'approved' } }>(
        `/admin/ingredient-moderation/alias/${id}/approve`,
        { method: 'POST' }
      ),
    rejectAlias: (id: string) =>
      request<{ data: { id: string; status: 'rejected' } }>(
        `/admin/ingredient-moderation/alias/${id}/reject`,
        { method: 'POST' }
      ),
  },
};

// Additional response types
export interface GenerateRecipesInput {
  prompt: string;
  count?: number;
  dietary_requirements?: string[];
  servings?: number;
  max_time_minutes?: number | null;
  difficulty?: 'easy' | 'medium' | 'hard' | null;
  cuisine?: string | null;
  guide_id?: string | null;
  auto_save?: boolean;
  auto_enrich?: boolean;
  /**
   * Phase 7.40.6 — drives whether family allergens are unioned in.
   * Phase 8.9 — 'none' bypasses preference aggregation entirely (generic
   * recipes for unknown guests / public templates). Server-side, sending
   * `mode: 'none'` skips both the family-member union and the caller's
   * own allergens / dietary tags. Pair with `dietary_requirements: []`
   * for a fully unconstrained generation.
   */
  mode?: 'solo' | 'family' | 'auto' | 'none';
}

export interface GeneratedRecipe {
  title: string;
  description: string;
  servings: { quantity: number; unit: string };
  timing: { prep_minutes: number; cook_minutes: number; total_minutes: number };
  difficulty: 'easy' | 'medium' | 'hard';
  ingredients: Array<{
    name: string;
    quantity?: number | null;
    unit?: string | null;
    preparation?: string | null;
    optional?: boolean;
  }>;
  instructions: Array<{
    step_number: number;
    instruction: string;
    duration_minutes?: number | null;
    tips?: string[] | null;
  }>;
  diet_tags: string[];
  taxonomy_tags: string[];
  equipment: string[];
  tips: string[];
  variations: string[];
}

export interface GenerateRecipesResponse {
  success: boolean;
  data: {
    recipes: Array<{
      generated: GeneratedRecipe;
      saved?: { id: string; status: string } | null;
    }>;
    generation_id: string;
    prompt_id: string;
    model_name: string;
    confidence: number;
    generation_notes?: string | null;
  };
}

export interface SaveGeneratedRecipeResponse {
  success: boolean;
  data: {
    saved: { id: string; status: string };
    enrichment_status: string;
  };
}

export interface GenerationSuggestionsResponse {
  success: boolean;
  data: {
    suggestions: Array<{
      category: string;
      prompts: string[];
    }>;
  };
}

export interface ImportResponse {
  success: boolean;
  data: {
    recipe: GeneratedRecipe;
    original_source?: string;
    confidence?: number;
  };
}

export interface BatchImportItem {
  type: 'url' | 'text' | 'image';
  input: { url?: string; text?: string; image?: string; mimeType?: string };
}

export interface BatchImportStatus {
  batchId: string;
  status: 'pending' | 'processing' | 'complete' | 'cancelled';
  totalItems: number;
  completedItems: number;
  successfulItems: number;
  failedItems: number;
  items: Array<{
    itemId: string;
    type: 'url' | 'text' | 'image';
    status: 'pending' | 'processing' | 'complete' | 'error';
    result?: { success: boolean; data: { recipe: GeneratedRecipe; original_source?: string; confidence?: number } };
    error?: string;
    fileName?: string;
    selected?: boolean;
  }>;
}

export interface EnrichmentResponse {
  job_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'needs_review';
  message?: string;
}

// Phase 7.8: Granular enrichment response
export interface GranularEnrichmentResponse {
  success: boolean;
  message: string;
  jobId?: string;
  data?: {
    nutrition?: Record<string, unknown>;
    health_tags?: string[];
    gut_health_score?: number;
    allergens?: string[];
    coverage?: number;
  };
}

export interface EnrichmentStatusResponse {
  job_id: string | null;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'needs_review' | 'in_progress' | 'pending';
  progress: {
    ingredients: boolean;
    instructions: boolean;
    nutrition: boolean;
    diet_assessment: boolean;
    taxonomy: boolean;
  };
  needs_review: boolean;
  retry_count: number;
  last_updated: string;
}

export interface UserAllergen {
  allergen: string;
  severity: 'mild' | 'moderate' | 'severe';
}

/**
 * Phase 8.0 — additive validation block returned by the preferences add
 * endpoints (`/users/preferences/{likes,dislikes,intolerances}`) when the
 * catalogue + LLM fallback validator has something to say about the inputs.
 *
 * Empty arrays for all three fields are equivalent to the block being absent:
 * the controllers omit it from the JSON response in that case.
 */
export interface IngredientValidationAccepted {
  input: string;
  canonical_name: string;
  kind: 'exact' | 'promoted';
  pending_review?: boolean;
}

export interface IngredientValidationSuggestion {
  input: string;
  suggestion: string;
  suggestion_distance: number;
}

export interface IngredientValidationRejected {
  input: string;
  reason: 'unknown' | 'rate_limited';
  accept_anyway_param: 'force=1';
}

export interface IngredientValidationBlock {
  accepted: IngredientValidationAccepted[];
  suggestions: IngredientValidationSuggestion[];
  rejected: IngredientValidationRejected[];
}

/** Wire shape of the `/users/preferences/{likes,dislikes,intolerances}` POSTs. */
interface IngredientAddApiResponse {
  success: boolean;
  data: UserPreferences;
  validation?: IngredientValidationBlock;
}

/** Caller-facing return value: preferences plus the optional validation block. */
export interface IngredientAddResult {
  preferences: UserPreferences;
  validation?: IngredientValidationBlock;
}

export interface UserPreferences {
  id: string;
  user_id: string;
  liked_ingredients: string[];
  disliked_ingredients: string[];
  allergens: UserAllergen[];
  /** Phase 7.39 — tolerated in small amounts, distributed across the week (not excluded) */
  intolerances: string[];
  dietary_tags: string[];
  measurement_display: 'metric' | 'imperial' | 'both';
  questionnaire_completed: boolean;
  // Phase 7.17: user's chosen default guide
  default_guide_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserFavourite {
  id: string;
  user_id: string;
  recipe_id: string;
  notes?: string;
  created_at: string;
  recipe?: Recipe;
}

export interface NutritionCalculationResponse {
  recipeId: string;
  perServing: NutritionData;
  per100g: NutritionData;
  allergens: string[];
  warnings: string[];
}

export interface UKNutritionLabel {
  recipeId: string;
  servingSize: string;
  perServing: NutritionData;
  per100g: NutritionData;
  referenceIntake: Record<string, number>;
  allergens: string[];
}

export interface NutritionSearchResponse {
  items: Array<{
    id: string;
    name: string;
    cofidCode?: string;
    nutritionPer100g: NutritionData;
  }>;
  total: number;
}

export interface HealthCheckResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  services: Record<string, { status: string; latency?: number }>;
}

// ============================================================================
// Menu Types (Phase 6C, Phase 6F)
// ============================================================================

export interface Menu {
  id: string;
  tenant_id: string;
  user_id: string;
  name: string;
  description?: string | null;
  status: 'draft' | 'active' | 'archived';
  start_date: string;
  end_date: string;
  is_active?: boolean;
  mode?: 'individual' | 'family_group';
  family_group_id?: string | null;
  guide_id?: string | null;
  default_servings?: number;
  num_people?: number;
  planned_meals?: string | null;  // JSON array e.g. '["breakfast","lunch","dinner"]'
  week_start_day?: string;
  /** Phase 7.31: persisted user guidance text (free-text steering) */
  guidance_text?: string | null;
  created_at: string;
  updated_at: string;
  days?: MenuDay[];
  _count?: { days: number };
}

export interface MenuDay {
  id: string;
  menu_id: string;
  day_number: number;
  date: string;
  recipes?: MenuRecipeItem[];
}

export interface MenuRecipeItem {
  id: string;
  menu_day_id: string;
  recipe_id: string;
  meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  servings: number;
  order: number;
  leftover_from?: string | null;
  recipe?: {
    id: string;
    title: string;
    image_thumbnail_url?: string | null;
    prep_time_minutes?: number | null;
    cook_time_minutes?: number | null;
    nutrition_data?: string | null;
  };
}

export interface CreateMenuInput {
  name: string;
  description?: string;
  numDays?: number;
  numPeople?: number;
  servings?: number;
  weekStartDay?: string;
  guideId?: string;
  plannedMeals?: ('breakfast' | 'lunch' | 'dinner' | 'snack')[];
  // Legacy: explicit date range (optional — dates computed from numDays if omitted)
  startDate?: string;
  endDate?: string;
  // Phase 7.40.7 — persists the user's planning mode (solo/family) onto the menu row.
  planningMode?: 'individual' | 'family_group';
  familyGroupId?: string;
}

export interface FillMenuWithAIInput {
  guideId?: string;
  prompt?: string;
}

/** Phase 7.31: payload for re-planning an existing menu with locked meals */
export interface AdjustMenuInput {
  additionalInstructions: string;
  lockedMealIds: string[];
}

export interface UpdateMenuInput {
  name?: string;
  description?: string;
  status?: 'draft' | 'active' | 'archived';
  startDate?: string;
  endDate?: string;
}

export interface AddRecipeToMenuInput {
  dayNumber: number;
  recipeId: string;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  servings?: number;
}

// Phase 6F: AI Generation
export interface GenerateMenuInput {
  mode: 'individual' | 'family_group';
  familyGroupId?: string;
  guideId?: string;
  weekStartDay?: string;
  startDate: string;
  endDate: string;
  meals: Array<{ dayNumber: number; mealType: string }>;
  presets?: Array<{ recipeId: string; dayNumber?: number; mealType?: string }>;
  servings?: number;
  prompt?: string;
}

export interface SwapRecipeInput {
  menuRecipeId: string;
  newRecipeId: string;
}

export interface RecipeAlternative {
  id: string;
  recipe_id: string;
  title: string;
  image_url?: string | null;
  thumbnail?: string | null;
  total_time_minutes?: number;
  cookTime?: number;
  servings?: number;
  similarity_score?: number;
  match_reason?: string;
  nutrition?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
  };
}

export interface MenuNutrition {
  daily: Array<{
    dayNumber: number;
    date: string;
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fibre: number;
  }>;
  weeklyAverage: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fibre: number;
  };
  ukGuidelines: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fibre: number;
  };
  warnings: string[];
}

export interface MenuResponse {
  data: Menu;
}

export interface MenuListResponse {
  data: Menu[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface MenuRecipeResponse {
  data: MenuRecipeItem;
}

export interface ShoppingListItem {
  ingredient: string;
  name: string;
  quantity: number;
  total_quantity?: number;
  unit: string;
  category: string;
  recipes: string[];
  used_in?: string[];
}

export interface ShoppingListGroup {
  category: string;
  ingredients: ShoppingListItem[];
  items: ShoppingListItem[];
}

export interface ShoppingListResponse {
  data: {
    menuId: string;
    menuName: string;
    name: string;
    dateRange: string;
    servings: number;
    groups: ShoppingListGroup[];
    items: ShoppingListItem[];
    totalItems: number;
  };
}

// Fix D: single-recipe shopping list
export interface RecipeShoppingListResponse {
  data: {
    recipeId: string;
    recipeName: string;
    name: string;
    servings: number;
    baseServings: number;
    groups: ShoppingListGroup[];
    items: ShoppingListItem[];
    totalItems: number;
  };
}

// Re-export types for convenience
export type { NutritionData } from '@/types/recipe';
// Guide types (Phase 6E)
export interface Guide {
  id: string;
  name: string;
  description: string;
  concise_prompt: string | null;
  scope: 'recipe' | 'meal_plan' | 'both';
  is_system: boolean;
  is_public: boolean;
  version: string;
  tenant_id: string | null;
  user_id: string | null;
  // Phase 7.17
  slug: string | null;
  sort_order: number;
  is_default_system: boolean;
  tags: string | null; // JSON array of health/dietary tag strings
  created_at: string;
  updated_at: string;
}

export interface CreateGuideInput {
  name: string;
  description: string;
  scope: 'recipe' | 'meal_plan' | 'both';
  is_public?: boolean;
}

export interface UpdateGuideInput {
  name?: string;
  description?: string;
  scope?: 'recipe' | 'meal_plan' | 'both';
  is_public?: boolean;
}

export interface GenerateGuideInput {
  diet_name: string;
  brief_description: string;
  scope: 'recipe' | 'meal_plan' | 'both';
}

export interface GuideResponse {
  data: Guide;
}

export interface GuidesListResponse {
  data: Guide[];
  meta: {
    total: number;
  };
}

export interface GuideDistillResponse {
  data: {
    concise_prompt: string;
  };
}

// ============================================================================
// Family Types (Phase 6D)
// ============================================================================

export interface FamilyMember {
  id: string;
  user_id: string;
  family_group_id: string;
  role: 'owner' | 'admin' | 'member';
  status: 'pending' | 'accepted' | 'declined';
  joined_at: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface FamilyGroup {
  id: string;
  tenant_id: string;
  name: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  members: FamilyMember[];
}

export interface FamilyInvitation {
  id: string;
  family_group_id: string;
  inviter_id: string;
  invitee_email: string;
  status: 'pending' | 'accepted' | 'declined';
  invited_at: string;
  // Phase 7.40.8 — GET /family-groups/invitations includes the related group
  // (Prisma `include: { group: true }`) so the header bell can render a name.
  group?: Pick<FamilyGroup, 'id' | 'name'>;
}
// Phase 7.22: Export types
export type ExportFormat = 'markdown' | 'html';
export type ExportFilter = 'all' | 'guide' | 'tag' | 'dietary' | 'status' | 'recipe';

export interface CreateExportJobInput {
  format: ExportFormat;
  filter: ExportFilter;
  filterId?: string;
  filterValue?: string;
}

export interface ExportJobResult {
  downloadToken: string;
  filename: string;
  recipeCount: number;
  format: ExportFormat;
}

export interface ExportJobStatusResponse {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  error?: string | null;
  result?: ExportJobResult | null;
  createdAt: string;
  completedAt?: string | null;
}